// ALL user-facing chrome strings live HERE (D1: English; a V7 language pass is a one-file flip).
// Pure module — no hooks, no React. V1 invariant: inputs are display labels; raw keys never enter.

export const CHROME = {
  kickerTimeline: 'Timeline',
  kickerSummary: 'Summary',
  forWord: 'for',
  inWord: 'in',
  groupedBy: 'Grouped by',
  /** v2's caption separator (U+2E30 ring point), space-padded. */
  valueSeparator: ' ⸰ ',
  /** En-dash date-range joiner for custom windows / pins. */
  rangeJoiner: ' – ',
} as const

/** chart_type -> kicker. Time-series cards read "Timeline", per-window/per-category totals "Summary". */
export const KICKER_BY_CHART_TYPE: Record<string, string> = {
  timeline: CHROME.kickerTimeline,
  dynamics_panels: CHROME.kickerTimeline,
  multi_timeframe_overlay: CHROME.kickerTimeline,
  absolute_bar: CHROME.kickerSummary,
  summary_bar_strip: CHROME.kickerSummary,
  metric_driver_bars: CHROME.kickerSummary,
}

export interface ChartTitleInput {
  /** composeChartTitle(cascade) output (live tiles) or useKpiDisplayLabel (pins — pass
   *  valueTypeLabel:null there; the pin label may already carry "(vt)" — never double-unit). */
  kpiTitle: string
  /** Display label of the SINGLE active value type; callers pass null unless exactly one. */
  valueTypeLabel?: string | null
  timeframePhrase?: string | null
}

export function buildChartTitle({
  kpiTitle,
  valueTypeLabel,
  timeframePhrase,
}: ChartTitleInput): string {
  let title = kpiTitle
  if (valueTypeLabel) title += ` ${CHROME.inWord} ${valueTypeLabel}`
  if (timeframePhrase) title += ` ${CHROME.forWord} ${timeframePhrase}`
  return title
}

export interface TimeframeLabelInput {
  /** Active preset key; 'custom' when absolute dates drive the window (incl. frames/campaigns). */
  preset: string
  /** preset key -> display label (from TimeframeSelect's PRESET_OPTIONS, passed in: pure module). */
  presetLabels: Record<string, string>
  fromIso: string
  toIso: string
  /** Fiscal frames + campaigns; custom bounds matching mirrors TimeframeSelect's selected-option rule. */
  absoluteOptions: Array<{ label: string; from: string; to: string }>
}

export function resolveTimeframeLabel(i: TimeframeLabelInput): string {
  const range = `${i.fromIso}${CHROME.rangeJoiner}${i.toIso}`
  if (i.preset !== 'custom') return i.presetLabels[i.preset] ?? range
  return i.absoluteOptions.find((o) => o.from === i.fromIso && o.to === i.toIso)?.label ?? range
}

/** Joins the timeframe labels a tile ACTUALLY renders (render-truthful: multi-TF tiles pass
 *  only their tf_list labels today; when V4 re-adds the main window its label simply joins). */
export function timeframePhrase(labels: string[]): string | null {
  return labels.length === 0 ? null : labels.join(', ')
}

export interface CaptionInput {
  /** Raw dim -> raw values (URL state). Dim NAMES render as-is (v3 dim_names are display-grade). */
  filters: Record<string, string[]>
  groupBy: string[]
  /** Raw value -> display label; fall back to the raw value when absent. */
  valueLabelFor?: (dim: string, value: string) => string
}

export function buildContextCaption(i: CaptionInput): string[] {
  const lines: string[] = []
  for (const [dim, values] of Object.entries(i.filters)) {
    if (values.length === 0) continue
    const labels = values.map((v) => i.valueLabelFor?.(dim, v) ?? v)
    lines.push(`${dim}: ${labels.join(CHROME.valueSeparator)}`)
  }
  if (i.groupBy.length > 0) {
    lines.push(`${CHROME.groupedBy} ${i.groupBy.join(CHROME.valueSeparator)}`)
  }
  return lines
}

export interface ExportMetadataInput {
  title: string
  kpi: string
  timeframePhrase: string
  fromIso: string
  toIso: string
  factTable: string
  captionLines: string[]
}

/** XLSX metadata rows — replaces the inline German arrays (keys unify to English chrome, D1). */
export function buildExportMetadata(i: ExportMetadataInput): Array<[string, string]> {
  return [
    ['Title', i.title],
    ['KPI', i.kpi],
    [
      'Timeframe',
      i.timeframePhrase.trim()
        ? `${i.timeframePhrase} (${i.fromIso}${CHROME.rangeJoiner}${i.toIso})`
        : `${i.fromIso}${CHROME.rangeJoiner}${i.toIso}`,
    ],
    ...(i.captionLines.length > 0
      ? ([[`Context`, i.captionLines.join(' | ')]] as Array<[string, string]>)
      : []),
    ['Fact table', i.factTable],
    ['Created', new Date().toISOString().slice(0, 10)],
  ]
}

// --- Row 23: filter-applicability notes (D4: generic wording; labels arrive pre-resolved) ---

export function filterDropsGroupsNote(
  dim: string,
  droppedLabels: string[],
  expectedCount: number,
): string {
  return `Filter "${dim}" not applicable to ${droppedLabels.length} of ${expectedCount} groups (${droppedLabels.join(', ')}) — excluded from this chart.`
}

export function filtersDropGroupsNote(droppedLabels: string[], expectedCount: number): string {
  return `Active filters exclude ${droppedLabels.length} of ${expectedCount} groups (${droppedLabels.join(', ')}).`
}

export function filterNoDataNote(dims: string[]): string {
  const quoted = dims.map((d) => `"${d}"`).join(', ')
  return dims.length === 1
    ? `Filter ${quoted} is not applicable to this KPI — no data matches.`
    : `Filters ${quoted} are not applicable to this KPI — no data matches.`
}

export const NOTE_FILTERS_MATCH_NO_DATA = 'Active filters match no data for this KPI.'

/** SP-1: dim applies to the numerator only — the denominator stays at its total. */
export function ratioDimNumOnlyNote(dim: string, denomLabel: string): string {
  return `Grouping by "${dim}" breaks out the numerator only — the denominator (${denomLabel}) stays at its total.`
}

/** SP-1: dim applies to the denominator only — the numerator is not broken out. */
export function ratioDimDenomOnlyNote(dim: string, numLabel: string): string {
  return `Grouping by "${dim}" breaks out the denominator only — the numerator (${numLabel}) is not broken out.`
}

/** Batch 3 P1: prune notice — dims that were in the URL but not supported by the active fact's
 *  dim catalog, stripped before any walk fires. */
export const prunedDimsNotice = (dims: string[]): string =>
  `Removed filter/grouping not available on this page: ${dims.join(', ')}.`

/** FU-31: a cross-page-carried KPI the destination page doesn't register was reset to the
 *  page default (mirrors prunedDimsNotice). DRAFT COPY — JL wording gate at the walkthrough. */
export const removedKpiNotice = (kpiLabel: string): string =>
  `Removed KPI not available on this page: ${kpiLabel}.`

/** Campaigns C1 (spec §4): shown in place of prunedDimsNotice when a campaign is active AND every
 *  pruned dim is one of its managed dims — the campaign's attribution filters don't apply to the
 *  current data, so only its time window is in effect. */
export const campaignPrunedNotice = (label: string): string =>
  `${label}: campaign filters don't apply to this data — showing its time window only.`

/** I1b partial case: explicitly-requested Actual axis dropped by a dim filter. */
export const actualAxisDropNote = (dims: string[]): string =>
  `Actual values aren't attributed to ${dims.map((d) => `"${d}"`).join(', ')} — the Actual series is hidden under this filter.`

/** I1b blank case: a campaign predicate is active and the walk returned nothing. */
export const campaignNoDataNotice = (label: string): string =>
  `No data matches ${label}'s campaign filters in this window.`

/** D-FEW-5 sharper I1b variant: the fact carries the Campaign dim but no row is tagged with
 *  THIS campaign's id (value absent from the fact's value list) — e.g. an umbrella campaign
 *  whose rows are tagged to its children. DRAFT COPY — JL wording gate at the walkthrough. */
export const campaignNoRowsNotice = (label: string): string =>
  `${label} has no rows of its own on this data — nothing here is tagged with it.`

/** C3b Task 5 (spec D-C3b-1): a ratio walk under a campaign resolved via ATTRIBUTION filters
 *  (factCarriesCampaign === false — one or both of the ratio's component facts don't carry the
 *  exact Campaign dim) — the figures aren't campaign-exact, they're everything matching the
 *  attribution filters in the window. Say so; the note retires itself once the resolution
 *  light-up flips factCarriesCampaign true (see campaignRatioScopeFor). Draft copy — exact
 *  wording is a JL gate at ship. */
export const campaignRatioScopeNote = (campaignLabel: string, predicateSummary: string): string =>
  `Campaign ${campaignLabel} applies as attribution filters (${predicateSummary}) on this measure — figures cover everything matching those attributes in the window, not the campaign alone.`

/** C3b I2/M8: the window-only variant of the ratio-scope note — used when the applied attribution
 *  set is EMPTY (a window-only campaign, or every attribution dim dropped by the ratio union prune),
 *  so there are no attribution filters to name. Without this the note rendered an empty parenthetical
 *  "()" (M8). The campaign contributes only its saved time window on this measure. Draft copy — exact
 *  wording is a JL gate at ship, alongside campaignRatioScopeNote. */
export const campaignRatioScopeWindowOnlyNote = (campaignLabel: string): string =>
  `Campaign ${campaignLabel} applies as its saved time window only on this measure — the figures are not otherwise filtered to the campaign.`

/** FU-m: group-by over a dim the walk's fact does not carry. `metricLabel` names the affected
 *  arm; the fallback string lives here so chartChrome stays the sole user-facing-string home. */
export function groupByNotApplicableNote(dims: string[], metricLabel?: string): string {
  const quoted = dims.map((d) => `"${d}"`).join(', ')
  const target = metricLabel && metricLabel.length > 0 ? metricLabel : 'this metric'
  return `Grouping by ${quoted} ${dims.length === 1 ? 'is' : 'are'} not applicable to ${target}.`
}

/** Primitive-only input — chartChrome stays decoupled from ReviewPage types. */
export interface ApplicabilityNoteInput {
  status: 'partial' | 'empty'
  expectedCount?: number
  /** filter dim -> dropped group DISPLAY labels (attributed). */
  byDimLabels?: Record<string, string[]>
  /** dropped group display labels not attributable to a single dim. */
  unattributedLabels?: string[]
  inapplicableDims?: string[]
}

export function buildApplicabilityNoteLines(i: ApplicabilityNoteInput): string[] {
  if (i.status === 'empty') {
    return [
      (i.inapplicableDims?.length ?? 0) > 0
        ? filterNoDataNote(i.inapplicableDims!)
        : NOTE_FILTERS_MATCH_NO_DATA,
    ]
  }
  const lines = Object.entries(i.byDimLabels ?? {}).map(([dim, labels]) =>
    filterDropsGroupsNote(dim, labels, i.expectedCount ?? 0),
  )
  if ((i.unattributedLabels?.length ?? 0) > 0) {
    lines.push(filtersDropGroupsNote(i.unattributedLabels!, i.expectedCount ?? 0))
  }
  return lines
}

/** Walkthrough catch (V6): a kpi carried across pages (tab nav preserves the URL) may have no
 *  registry rows on the target page — say so instead of rendering a blank page. */
export function noChartsForKpiNote(kpi: string): string {
  return `No charts are registered for "${kpi}" on this page — pick a KPI from the selector.`
}

// --- V7 (row 9): PanelStatRow chip strings (formerly ControlLabel, wave-3 §2/D3) ---
export const CHIP = {
  comparisonChart: 'Comparison Chart',
  // Wave-4 §2 (D3): signed chips — arrow carries the sign, so callers pass |growth|.
  growthUp: (formattedPercent: string) => `▲ ${formattedPercent}%`,
  growthDown: (formattedPercent: string) => `▼ ${formattedPercent}%`,
  growthZero: 'Δ 0%',
} as const

// --- V7 (row 9): app-wide UI strings. ONE home; sections mirror UI areas. ---

export const CONTROLS = {
  kpi: 'KPI',
  measure: 'Measure',
  knobCumulativeForced: 'Auto-active for stacked facets',
  knobCumulativeTooltip: 'Cumulative sum over time (Σ; for ratios Σnumerator/Σdenominator)',
  valueType: 'Value Type',
  driver: 'Driver',
  timeframe: 'Timeframe',
  granularity: 'Granularity',
  from: 'From',
  to: 'To',
  addFilter: '+ Filter',
  allFiltersActive: 'All filters active',
  alreadyActive: 'already active',
  groupBy: 'Group by',
  columns: 'Columns',
  columnsNone: 'None',
  columnsNeedsGroupBy: 'Select a group-by dimension first',
  addTimeframe: 'Add Timeframe',
  resetSettings: 'Reset Settings',
  compareWith: 'Compare with',
  compareOff: 'Off',
  comparePreviousPeriod: 'Previous period',
  compareOtherMetric: 'Other metric',
  compareSecondMetric: '2nd metric',
  comparisonPeriod: 'Comparison period',
  previousPeriodBaseline: 'Previous-period baseline',
  contributionViewAria: 'Contribution view',
  mediaChannels: 'Media Channels',
  driverGroups: 'Driver Groups',
  removeAria: 'remove',
  perChartPanel: (dots: number, unitLabel: string) => `${dots} ${unitLabel} per chart panel`,
  knobRunningTotal: 'Running total',
  knobCumulative: 'Cumulative',
  knobShowPercentage: 'Show Percentage',
  /** Task 4 (chart-semantics): stacked-facet denial reasons, keyed to AdditivityReason. */
  knobStackedDeniedVt: "Value types don't sum — stacking unavailable for mixed value types",
  knobStackedDeniedDim: 'Not stackable by this grouping',
  knobStackedDeniedPending: 'Checking stackability…',
  /** Task 4: Show-Percentage denial reasons — additivity reason wins when both apply. */
  knobPercentageDeniedAdditivity: 'Percentages need a summable selection',
  knobPercentageDeniedNegative: 'Percentages are unavailable when values are negative',
  campaignHasFilters: 'Applies campaign filters',
  /** FU-32: ordinary-chip honesty marker — the chip's dim is campaign-owned and an active
   *  campaign's resolution governs what actually fires. Dim-governed phrasing (JL wording
   *  gate 2026-07-14): stays truthful on the empty just-added ghost chip too, where the
   *  value-applied framing would mislead (a divergent pick collapses the campaign). */
  campaignCoveredChip: 'This dimension is governed by the active campaign',
  /** FU-08: window-only campaign badge in the timeframe picker (draft copy; JL wording gate
   *  deferred to the vodafone walkthrough — greencrate has no window-only campaign to show).
   *  Phrasing family: CARDS.windowOnlySuffix / campaignRatioScopeWindowOnlyNote. */
  campaignWindowOnly: 'Applies time window only',
} as const

export const CARDS = {
  useMainTimeframeHint: 'The main timeframe above applies.',
  /** Campaigns C1 Task 7: appended to a multi-TF arm's label when its campaign id no longer
   *  resolves in controls (re-cut vintage) — the arm still renders, dates-only, degradation
   *  stays visible rather than silently dropping the predicate. */
  windowOnlySuffix: ' (window only)',
} as const

export const MESSAGES = {
  retry: 'Retry',
  chartLoading: 'Loading chart…',
  chartUpdating: 'Updating…',
  chartLoadFailed: 'Chart could not be loaded.',
  chartsLoadFailed: 'Charts could not be loaded.',
  chartDataLoadFailed: 'Chart data could not be loaded.',
  controlsLoadFailed: 'Controls could not be loaded.',
  noKpisConfigured: 'No metrics are configured for this page.',
  noDateRangeForFact: 'No date range available for this fact table — check the data.',
  unsupportedChartType: (types: string) => `Unsupported chart type: ${types}`,
  unsupportedPinEntry: (walkFn: string, chartType: string) =>
    `Unsupported entry: ${walkFn} / ${chartType}`,
  /** FU-03: a pinned campaign-exact filter the driver fact cannot carry — a stripped replay
   *  would silently widen to unfiltered totals, so the driver walk is not fired at all. */
  campaignFilterNotApplicable: (driverLabel?: string) =>
    `The pinned campaign filter is not applicable to ${
      driverLabel && driverLabel.length > 0 ? driverLabel : 'this metric'
    } — the driver chart is omitted.`,
  noDataForFilters: 'No data for these filters',
  previousPeriod: 'Previous period',
  noDataInPreviousPeriod: 'No data in the previous period',
  noComparisonData: (label: string) => `No comparison data for ${label} in the selected timeframe.`,
  comparisonDataLoadFailed: 'Comparison data could not be loaded.',
  noChartConfiguredForKpi: 'No chart is configured for this metric.',
  chartConfigLoadFailed: 'Could not load chart configuration',
  chartNotConfigured: (walkFn: string) =>
    `This chart is not configured (no chart_registry entry for ${walkFn}).`,
  /** EmptyChartState window labels (primary window vs compare window). */
  windowLabelPrimary: 'timeframe',
  windowLabelCompare: 'comparison period',
  noDataInWindow: (label: string, range: string) => `No data in the ${label} (${range}).`,
  adjustWindowHint: (label: string) => `Adjust the ${label} above.`,
  hiddenRemainder: (count: number) =>
    count === 1
      ? 'Last partial period (1 data point) hidden — the timeframe is not divisible by the panel size.'
      : `Last partial period (${count} data points) hidden — the timeframe is not divisible by the panel size.`,
  truncatedPanels: (shown: number, natural: number) =>
    `Showing ${shown} of ${natural} panels — the timeframe exceeds the current granularity.`,
  showMore: '+ Show more',
  resetCap: '← Reset cap',
  showFewer: 'Show fewer',
  showNMore: (n: number) => `+ ${n} more`,
  dimCatalogLoadFailed: 'Could not load the dim catalog — filters disabled.',
  noDimCatalogEntries: (factTable: string) =>
    `No dim-catalog entries for ${factTable}. Contact an admin or check validate_dim_coverage.`,
  duplicateNameError: 'An entry with this name already exists.',
  timeframeTooLarge: 'Timeframe too large — shorten the timeframe or coarsen the granularity.',
  addTimeframeHint: 'Add a timeframe above to overlay it with the main timeframe.',
  overviewLoadFailed: 'Overview could not be loaded.',
} as const

/** Collections / Pin / dialog strings — extended by the sweep tasks as they reach each file. */
export const DIALOGS = {
  cancel: 'Cancel',
  save: 'Save',
  delete: 'Delete',
  close: 'Close',
  loading: 'Loading…',
  pinToCollection: 'Pin to collection',
  pinFailed: 'Pinning failed. Please retry.',
  // PinButton
  couldNotLoadCollections: 'Could not load collections',
  createNewCollection: '+ Create new collection…',
  pinNameOptional: 'Pin name (optional)',
  // CollectionShareDialog
  shareCollectionTitle: (name: string) => `Share collection — ${name}`,
  sharedWith: 'Shared with:',
  notShared: 'This collection is not shared with anyone.',
  stopSharing: 'Stop sharing',
  addPerson: 'Add person',
  // PinEditDialog
  editPin: 'Edit pin',
  title: 'Title',
  description: 'Description',
  // CollectionDeleteConfirmDialog
  deleteCollectionTitle: 'Delete collection',
  deleteCollectionQuestion: (name: string) => `Delete collection "${name}"?`,
  pinsAlsoDeleted: (n: number | null | undefined) =>
    n == null
      ? 'All pins will also be deleted.'
      : n === 0
        ? 'This collection contains no pins.'
        : n === 1
          ? '1 pin will also be deleted.'
          : `All ${n} pins will also be deleted.`,
  // CollectionRenameDialog
  renameCollection: 'Rename collection',
  name: 'Name',
  // ExportMenu
  exportAria: 'Export',
  exportLayoutJson: 'Export layout (JSON)',
  exportData: 'Export data',
  exportAsImage: 'Export as image',
  exportAsPdf: 'Export as PDF',
  exportFailed: 'Export failed. Please retry.',
  // PageExportButtons
  exportImage: 'Image',
  exportPdf: 'PDF',
  // Home
  dashboardPageTitle: 'Dashboard',
  // CollectionList — secondary label for a row that is the dashboard collection
  dashboardLabel: 'Dashboard',
  // CollectionList
  collectionsTabsAria: 'Collections Owner / Shared',
  newCollectionTitle: 'New collection',
  couldNotCreateCollection: 'Could not create collection',
  unmarkDashboard: 'Remove dashboard mark',
  markDashboard: 'Mark as dashboard',
  openCollection: 'Open collection',
  shareCollection: 'Share collection',
  removeAsDashboardFirst: 'Remove as dashboard first',
  dashboardCollection: 'Dashboard collection',
  collection: 'Collection',
  ownedTab: 'My collections',
  sharedTab: 'Shared',
  collectionsTitle: 'Collections',
  sharedCollectionsTitle: 'Shared collections',
  createButton: 'New',
  sharedBy: (nameOrId: string) => `Shared by ${nameOrId}`,
  sharedWithCount: (count: number) => `Shared with ${count}`,
  noCollectionsOwned: 'No collections yet — create one to collect pins.',
  noCollectionsShared: 'No collections have been shared with you yet.',
  couldNotUpdateDashboard: 'Could not update dashboard mark',
  rename: 'Rename',
  // CollectionItem
  couldNotRemovePin: 'Could not remove pin',
  pinActionsAria: (t: string) => `Pin actions for ${t}`,
  pinLoadFailed: 'Pin could not be loaded.',
  removeFromCollection: 'Remove from collection',
  pinEditMenuLabel: 'Edit pin',
  moveUp: 'Move up',
  moveDown: 'Move down',
  couldNotMoveUp: 'Could not move pin up',
  couldNotMoveDown: 'Could not move pin down',
  couldNotLoadPins: 'Could not load pins',
  // Home
  emptyDashboardHint: 'Create a collection and mark it as dashboard to see it here.',
  noDashboardYet: 'No dashboard set up yet.',
  toDashboard: 'Go to collections',
  emptyDashboard: 'This dashboard is empty. Pin charts on the review pages.',
  // Account/Password
  resetPasswordHint: 'Reset your password via Auth0.',
  resetPassword: 'Reset password',
  loggedInAs: (email: string) => `Logged in as ${email}`,
  signOut: 'Sign out',
  // Account/Theming
  themingHint: 'Local override of the tenant default. Saved automatically in the browser.',
  // Callback
  signingIn: 'Signing in…',
  // StalenessIndicator
  dataUpdated: 'Data has been updated. Click "Refresh" to load the latest version.',
  refresh: 'Refresh',
} as const

/** Navigation labels — one home for all nav strings so a language flip is a one-file change. */
export const NAV = {
  home: 'Home',
  reviewStatus: 'Review Status',
  reviewPerformance: 'Review Performance',
  collections: 'Collections',
  profile: 'Profile',
  account: 'Account',
  logout: 'Logout',
  theming: 'Theming',
  password: 'Password',
  // Account/Theming labels
  brandColorLabel: 'Brand color',
  chipPaletteLabel: 'Chip palette',
} as const

export const GRANULARITY = {
  unitSingular: {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    quarter: 'Quarter',
    year: 'Calendar Year',
    fy: 'Fiscal Year',
  },
  unitPlural: {
    day: 'Days',
    week: 'Weeks',
    month: 'Months',
    quarter: 'Quarters',
    year: 'Calendar Years',
    fy: 'Fiscal Years',
  },
  // Adverb form stays 'Yearly': it renders only in size warnings ("… too large for daily values")
  // where the calendar/fiscal PHASE distinction is irrelevant.
  unitAdverb: {
    day: 'Daily',
    week: 'Weekly',
    month: 'Monthly',
    quarter: 'Quarterly',
    year: 'Yearly',
    fy: 'Fiscal-year',
  },
  unitNoun: {
    day: 'daily values',
    week: 'weekly values',
    month: 'monthly values',
    quarter: 'quarterly values',
    year: 'calendar-year values',
    fy: 'fiscal-year values',
  },
  /** Copy GREENLIT by JL at the 2026-08-03 walkthrough — changes are CR territory. */
  partialFyMarker: '*',
  partialFyNote: (labels: readonly string[]) =>
    `${labels.join(' and ')} ${labels.length === 1 ? 'is' : 'are'} partial — the selected timeframe covers only part of ${labels.length === 1 ? 'that fiscal year' : 'those fiscal years'}.`,
} as const

export const TIMEFRAME_PRESET_LABELS: Record<string, string> = {
  last_4_weeks: 'Last 4 Weeks',
  last_12_weeks: 'Last 12 Weeks',
  last_quarter: 'Last Quarter',
  ytd: 'CY YTD',
  fy_ytd: 'FY YTD',
  last_year: 'Last Year',
  all_time: 'Model Time',
  custom: 'Custom',
}

export const TIMEFRAME_GROUPS = {
  presets: 'Presets',
  fixed: 'Fixed Frames',
  rolling: 'Rolling Frames',
  campaigns: 'Campaigns',
} as const
